import { ArrowRight, BookOpen, Compass, FileEdit, Map, MountainSnow, Plus, Route } from "lucide-react";
import type { CSSProperties } from "react";
import type { TripSummary, User } from "../types";

interface Props {
  trips: TripSummary[];
  user: User | null;
  canCreate: boolean;
  onOpenLibrary: () => void;
  onOpenRoute: (id: string) => void;
  onCreate: () => void;
  onLogin?: () => void;
  onAccount?: () => void;
}

const statusCopy = { DRAFT: "继续编排", IN_REVIEW: "等待审阅", PUBLISHED: "查看成稿", ARCHIVED: "查看归档" } as const;

export function ProductHome({ trips, user, canCreate, onOpenLibrary, onOpenRoute, onCreate, onLogin, onAccount }: Props) {
  const active = trips.filter((trip) => trip.status !== "ARCHIVED");
  const published = trips.filter((trip) => trip.status === "PUBLISHED");
  const recent = active.slice(0, 3);

  return (
    <main className="product-home">
      <header className="home-header">
        <button className="home-brand" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}><MountainSnow size={24} /><span>山河行迹</span></button>
        <nav aria-label="首页导航"><button onClick={onOpenLibrary}>路线库</button><a href="#workflow">如何规划</a>{user && onAccount ? <button onClick={onAccount}>{user.name} · 账号</button> : !user && onLogin && <button onClick={onLogin}>登录</button>}</nav>
      </header>

      <section className="home-hero">
        <div className="home-hero-copy">
          <p>从灵感到出发，一张图完成</p>
          <h1>把想去的地方，<br />编成走得通的旅程。</h1>
          <span>逐日安排地点、道路、停留与海拔。草稿放心修改，成稿安心分享。</span>
          <div>{canCreate && <button className="home-primary" onClick={onCreate}><Plus size={18} />规划新路线</button>}<button className="home-secondary" onClick={onOpenLibrary}><BookOpen size={18} />打开路线库</button></div>
        </div>
        <div className="home-map-sheet" aria-label="路线规划示意">
          <div className="contour contour-one" /><div className="contour contour-two" />
          <span className="map-place place-one">康定</span><span className="map-place place-two">新都桥</span><span className="map-place place-three">理塘</span>
          <svg viewBox="0 0 520 430" aria-hidden="true"><path d="M82 306 C138 250 148 175 236 202 S342 278 440 116" /><circle cx="82" cy="306" r="7" /><circle cx="236" cy="202" r="7" /><circle cx="440" cy="116" r="7" /></svg>
          <aside><small>川西环线 · 第 3 天</small><strong>新都桥 → 理塘</strong><span>约 196 km　4 小时 12 分</span></aside>
        </div>
      </section>

      <section className="home-route-section">
        <div className="home-section-heading"><div><p>你的旅行档案</p><h2>{user ? `欢迎回来，${user.name}` : "公开路线"}</h2></div><button onClick={onOpenLibrary}>查看全部 <ArrowRight size={16} /></button></div>
        <div className="home-route-grid">
          {recent.map((trip, index) => <button key={trip.id} className="home-route-card" onClick={() => onOpenRoute(trip.id)} style={{ "--route-color": trip.accent || "#3977A8" } as CSSProperties}>
            <span>{String(index + 1).padStart(2, "0")}</span><div><small>{statusCopy[trip.status]}</small><h3>{trip.name}</h3><p>{trip.short || `${trip.dayCount} 天路线，包含 ${trip.placeCount} 个地点`}</p></div><ArrowRight size={18} />
          </button>)}
          {!recent.length && <div className="home-route-empty"><Compass size={25} /><strong>路线库还是空的</strong><span>{canCreate ? "建立第一条路线，从第一个想去的地点开始。" : "登录后继续自己的路线，或稍后看看公开作品。"}</span></div>}
        </div>
        <div className="home-stats"><span><strong>{active.length}</strong>条进行中的路线</span><span><strong>{published.length}</strong>条已发布成稿</span><span><strong>{trips.reduce((total, trip) => total + trip.placeCount, 0)}</strong>个收录地点</span></div>
      </section>

      <section className="home-workflow" id="workflow">
        <div className="home-section-heading"><div><p>路线的生命周期</p><h2>灵感会变，成稿不被半成品覆盖</h2></div></div>
        <div className="workflow-line">
          <article><FileEdit size={20} /><span>草稿</span><p>搜地点、排日期、算道路，随时自动保存。</p></article>
          <article><Compass size={20} /><span>审阅</span><p>同行者核对节奏、风险与遗漏，再决定发布。</p></article>
          <article><Route size={20} /><span>发布</span><p>形成不可直接改写的只读成稿，按范围分享。</p></article>
          <article><Map size={20} /><span>派生</span><p>复制成稿为新草稿，保留公开版本继续规划。</p></article>
        </div>
      </section>
    </main>
  );
}
